/* eslint-disable indent */
/**
 * Student Table with Tabulator - Remote Pagination
 *
 * @package WPCore
 */

import { initCustomPagination } from '../tabulator/custom-pagination.js';

/**
 * Get filter elements from container
 * @param {HTMLElement} container - Filter container element
 * @returns {Object} Object containing searchInput and subscriptionSelect elements
 */
function getFilterElements(container) {
	return {
		searchInput: container.querySelector('input[name="search"]'),
		statusSelect: container.querySelector('select[name="status"]'),
		subscriptionSelect: container.querySelector('select[name="subscription"]')
	};
}

/**
 * Build AJAX parameters for Tabulator remote pagination
 * @param {HTMLInputElement} searchInput - Search input element
 * @param {HTMLSelectElement} subscriptionSelect - Subscription select element
 * @returns {Function} Function that returns filter params
 */
function buildAjaxParams(searchInput, statusSelect, subscriptionSelect) {
	return () => {
		const params = {
			action: 'load_student_data',
			nonce: wpcore.nonce,
			subscription: subscriptionSelect ? subscriptionSelect.value : 'all',
			status: statusSelect ? statusSelect.value : 'all'
		};

		// Add search param if exists
		if (searchInput && searchInput.value.trim()) {
			params.search = searchInput.value.trim();
		}

		return params;
	};
}

/**
 * Setup filter event listeners for remote pagination
 * @param {Object} table - Tabulator instance
 * @param {HTMLInputElement} searchInput - Search input element
 * @param {HTMLSelectElement} subscriptionSelect - Subscription select element
 */
function setupFilterListeners(table, searchInput, statusSelect, subscriptionSelect) {
	// Reload table with current filter values
	const reloadTable = () => {
		// Reset to page 1 and reload data
		table.setPage(1);
	};

	// Handle status filter change
	if (statusSelect) {
		statusSelect.addEventListener('change', reloadTable);
	}

	// Handle subscription filter change
	if (subscriptionSelect) {
		subscriptionSelect.addEventListener('change', reloadTable);
	}

	// Handle search input with debounce
	if (searchInput) {
		let searchTimeout;
		searchInput.addEventListener('input', () => {
			clearTimeout(searchTimeout);
			searchTimeout = setTimeout(reloadTable, 500); // 500ms debounce
		});
	}
}

export async function initStudentTable() {
	// Only run on student management page
	const tableContainer = document.getElementById('student-table');
	if (!tableContainer) return;

	// Get filter container first to build params
	const filterContainer = document.getElementById('student-filter');
	const { searchInput, statusSelect, subscriptionSelect } = filterContainer
		? getFilterElements(filterContainer)
		: { searchInput: null, statusSelect: null, subscriptionSelect: null };

	// Initialize Tabulator with remote pagination
	// eslint-disable-next-line no-undef
	const table = new Tabulator('#student-table', {
		// Remote pagination config
		ajaxURL: wpcore.ajaxUrl,
		ajaxConfig: 'POST',
		ajaxContentType: {
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded'
			},
			body: function (_url, _config, params) {
				// Convert params to URLSearchParams for proper POST
				return new URLSearchParams(params).toString();
			}
		},
		ajaxParams: buildAjaxParams(searchInput, statusSelect, subscriptionSelect),
		// Loading indicator
		dataLoader: true,
		dataLoaderLoading: '<div class="tabulator-loading-overlay"><div class="tabulator-loader"></div></div>',
		dataLoaderError: '<div class="tabulator-loading-overlay"><div class="text-red-500">' + wpcore.direct_t({ en: 'Error loading data', fr: 'Erreur de chargement des données' }) + '</div></div>',
		// Pagination config - remote mode
		paginationMode: 'remote',
		pagination: true,
		paginationSize: 10,
		placeholder: wpcore.direct_t({ en: 'No students found', fr: 'Aucun étudiant trouvé' }),
		layout: 'fitColumns',
		columns: [
			{
				title: wpcore.direct_t({ en: 'Student', fr: 'Étudiant' }),
				field: 'display_name',
				width: 250,
				headerSort: false,
				vertAlign: 'middle',
				formatter: (cell) => cell.getValue() // HTML rendered server-side
			},
			{
				title: wpcore.direct_t({ en: 'Status', fr: 'Statut' }),
				field: 'status',
				width: 200,
				hozAlign: 'center',
				headerHozAlign: 'center',
				headerSort: false,
				vertAlign: 'middle',
				formatter: (cell) => cell.getValue() // HTML rendered server-side
			},
			{
				title: wpcore.direct_t({ en: 'Current Subscription', fr: 'Abonnement actuel' }),
				field: 'current_subscription',
				width: 200,
				hozAlign: 'center',
				headerHozAlign: 'center',
				headerSort: false,
				vertAlign: 'middle',
				formatter: (cell) => cell.getValue() // HTML rendered server-side
			},
			{
				title: wpcore.direct_t({ en: 'Current Weight', fr: 'Poids actuel' }),
				field: 'current_weight',
				width: 150,
				hozAlign: 'center',
				headerHozAlign: 'center',
				headerSort: false,
				vertAlign: 'middle',
				formatter: (cell) => cell.getValue() // Plain text from server
			},
			{
				title: wpcore.direct_t({ en: 'Current Stage', fr: 'Étape actuelle' }),
				field: 'current_stage',
				width: 150,
				hozAlign: 'center',
				headerHozAlign: 'center',
				headerSort: false,
				vertAlign: 'middle',
				formatter: (cell) => cell.getValue() // HTML rendered server-side
			},
			{
				title: wpcore.direct_t({ en: 'Progress', fr: 'Progression' }),
				field: 'progress',
				width: 150,
				hozAlign: 'center',
				headerHozAlign: 'center',
				headerSort: false,
				vertAlign: 'middle',
				formatter: (cell) => cell.getValue() // HTML rendered server-side
			},
			{
				title: wpcore.direct_t({ en: 'Action', fr: 'Action' }),
				field: 'journey_id',
				hozAlign: 'center',
				minWidth: 150,
				headerHozAlign: 'center',
				vertAlign: 'middle',
				headerSort: false,
				formatter: (cell) => cell.getValue() // HTML rendered server-side
			}
		]
	});

	// Initialize custom pagination (reusable module)
	await initCustomPagination(table, {
		tableElement: tableContainer,
		containerId: 'student-pagination'
	});

	// Setup filter listeners
	setupFilterListeners(table, searchInput, statusSelect, subscriptionSelect);

	table.on('rowClick', (e, row) => {
		const data = row.getData();
		if (data.detail_url) {
			window.location.href = data.detail_url;
		}
	});
}
